/**
 * Recovery mode banner — ADR-0031 §Constrained recovery session.
 *
 * Persistent amber 'lock_reset' banner when a constrained recovery session is active.
 * "Recovery mode — finish setting up a sign-in method."
 *
 * The constrained session cannot navigate to banking surfaces; this banner
 * persists until the user completes factor enrollment.
 *
 * Iconography: lock_reset with the warning status token (ADR-0031 iconography vocabulary).
 */
import {
  ChangeDetectionStrategy,
  Component,
  inject,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoDirective, provideTranslocoScope } from '@jsverse/transloco';
import { RecoverySessionStore } from './recovery-session.store';

@Component({
  selector: 'tb-recovery-mode-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, RouterLink],
  providers: [provideTranslocoScope('security')],
  template: `
    <ng-container *transloco="let t; read: 'security'">
    @if (store.isActive()) {
      <div
        class="recovery-banner"
        role="alert"
        aria-live="assertive"
        data-testid="recovery-mode-banner"
      >
        <span class="banner-icon material-symbols-outlined" aria-hidden="true">lock_reset</span>
        <span class="banner-text">
          <strong>{{ t('recoveryBanner.title') }}</strong>{{ t('recoveryBanner.body') }}
        </span>
        <a class="banner-action" routerLink="/recovery/enroll" [attr.aria-label]="t('recoveryBanner.actionAria')">
          {{ t('recoveryBanner.action') }}
        </a>
      </div>
    }
    </ng-container>
  `,
  styles: `
    :host { display: block; }

    .recovery-banner {
      display: flex; align-items: center; gap: 0.65rem;
      padding: 0.65rem 1rem; font-size: 0.85rem; font-weight: 500;
      background: color-mix(in srgb, var(--tb-status-warning, #e0af68) 16%, transparent);
      border-bottom: 1px solid color-mix(in srgb, var(--tb-status-warning, #e0af68) 38%, transparent);
      color: var(--tb-status-warning, #e0af68);
    }

    .banner-icon { font-size: 1.1rem; flex-shrink: 0; }
    .banner-text { flex: 1; color: inherit; }
    .banner-text strong { font-weight: 700; }

    .banner-action {
      text-decoration: none; font-weight: 700; white-space: nowrap;
      padding: 0.2rem 0.55rem; border-radius: 4px;
      border: 1px solid currentColor; color: inherit;
      font-size: 0.8rem;
    }
    .banner-action:hover { opacity: 0.75; }
    .banner-action:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
  `,
})
export class RecoveryModeBannerComponent {
  readonly store = inject(RecoverySessionStore);
}
